import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import { clearHomePageInfosClientCache, getHomePageInfos, getTradingAccountsTrx } from "../../../utils/apiClient";
import { createDepositDemoOperation } from "../../../controller/accountsDemo/createDepositDemoController";
import AppConstants from "../../../utils/appConstants";
import { getPermissionControllers, hasCustomerPermission, isAccountApprovalLocked } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import { AccountType } from "../../../utils/enums";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";
import { getFlagCountryCodeForWallet } from "../../../utils/currencyFlagHelper";
import "../../css/cachIn.css";

/** Extract a trading account nickname from a TRX wallet item (empty when none). */
function getAccountNickname(item) {
  const raw =
    item?.account?.account_information?.NAME ||
    item?.account?.accountInformation?.name ||
    item?.NICK ||
    item?.NICK_NAME ||
    item?.nick ||
    item?.nick_name ||
    item?.NICKNAME ||
    "";
  const trimmed = String(raw).trim();
  return trimmed === "-" ? "" : trimmed;
}

export default function DepositDemo() {
  const { t } = useTranslation();
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  const [amount, setAmount] = useState("");
  const [toWallet, setToWallet] = useState(null);
  const [toWalletOptions, setToWalletOptions] = useState([]);

  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);

  // Wallet id passed from the accounts list to preselect the demo account.
  const preselectWalletId = location.state?.walletId ?? null;

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { customerPerm, customerDevicePerm } = permissions;

  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);

  const canDepositDemo =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_DEPOSIT_DEMO
    );

  /** Build dropdown options from the demo trading accounts (mirrors Flutter DepositDemoScreen). */
  const buildDemoOptions = useCallback((list) => {
    const options = [];
    (Array.isArray(list) ? list : []).forEach((item) => {
      // Currency type: 0=FIAT, 3=TRADING (matches Flutter CurrencyType enum)
      const currencyType =
        item.currencies?.TYPE ??
        item.currency?.type ??
        item.CURRENCY_TYPE ??
        item.currencyType ??
        null;

      // Account type: 2=DEMO (matches Flutter AccountType enum)
      const accountType =
        item.account?.TYPE ??
        item.account?.type ??
        item.ACCOUNT_TYPE ??
        item.accountType ??
        null;

      const tradingId =
        item.account?.account_information?.TRADING_ID ||
        item.account?.accountInformation?.tradingId ||
        item.TRADING_ID ||
        item.trading_id ||
        null;

      // Only keep TRADING wallets that belong to a DEMO account.
      const isTradingWallet = currencyType === 3 || (currencyType === null && tradingId);
      const isDemoAccount = accountType === AccountType.DEMO || (accountType === null && tradingId);
      if (!isTradingWallet || !isDemoAccount) return;

      const balance =
        item.AMOUNT ||
        item.CURRENT_BALENCE ||
        item.CURRENT_BALANCE ||
        item.BALANCE ||
        0;
      const currencyCode =
        item.currencies?.CURRENCY_CODE ||
        item.currency?.currencyCode ||
        item.CURRENCY_CODE ||
        "";
      const currencySymbol =
        item.currencies?.CURRENCY_SYMBOL ||
        item.currency?.currencySymbol ||
        item.CURRENCY_SYMBOL ||
        "";
      const numericBalance =
        typeof balance === "string" ? parseFloat(balance.replace(/,/g, "")) : parseFloat(balance);
      const formattedBalance = Number.isNaN(numericBalance) ? 0 : numericBalance;

      const value = item.ID ?? item.id ?? item.WALLET_ID;
      const nickname = getAccountNickname(item);
      const accountLabel = nickname ? `${tradingId || "-"}-${nickname}` : `${tradingId || "-"}`;
      const label = `${accountLabel} (${formattedBalance.toFixed(2)} ${currencySymbol || currencyCode})`;

      options.push({ value, label, data: item });
    });
    return options;
  }, []);

  const loadDemoAccounts = useCallback(async () => {
    const walletsResponse = await getTradingAccountsTrx([AccountType.DEMO], 0);
    const walletsData = walletsResponse?.decryptedData || walletsResponse?.data;
    if (walletsData && walletsData.CODE === AppConstants.responseOk && walletsData.DATA) {
      return buildDemoOptions(walletsData.DATA);
    }
    return [];
  }, [buildDemoOptions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErrors({});

        // Try cached user data first.
        let currentUser = null;
        try {
          const storedUserData = sessionStorage.getItem("userData");
          const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) {
              currentUser = JSON.parse(storedUserData);
              setUser(currentUser);
            }
          }
        } catch (_) {}

        try {
          const userResponse = await getHomePageInfos();
          const userData = userResponse?.decryptedData || userResponse?.data;
          if (userData && userData.CODE === AppConstants.responseOk && userData.DATA) {
            currentUser = userData.DATA;
            setUser(currentUser);
            try {
              sessionStorage.setItem("userData", JSON.stringify(currentUser));
              sessionStorage.setItem("userDataTimestamp", Date.now().toString());
            } catch (_) {}
          }
        } catch (_) {
          // Keep cached user when request fails.
        }

        const options = await loadDemoAccounts();
        setToWalletOptions(options);

        // Preselect the wallet coming from the accounts list, when present.
        if (preselectWalletId != null) {
          const match = options.find((o) => String(o.value) === String(preselectWalletId));
          if (match) setToWallet(match);
        }
      } catch (error) {
        showError("Error Occurred", error.message || "Failed to load data");
        setToWalletOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    if (errors.amount) setErrors({ ...errors, amount: null });
  };

  const handleToWalletChange = (value) => {
    setToWallet(value);
    if (errors.toWallet) setErrors({ ...errors, toWallet: null });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!amount || amount.trim() === "") {
      newErrors.amount = t("please_enter_the_amount", { ns: "mobile", defaultValue: "Amount is required" });
    } else {
      const value = parseFloat(amount.replace(/,/g, "").trim());
      if (Number.isNaN(value) || value <= 0) {
        newErrors.amount = t("please_enter_valid_amount", {
          ns: "mobile",
          defaultValue: "Amount must be a valid positive number",
        });
      }
    }
    if (!toWallet) {
      newErrors.toWallet = t("select_an_account", { ns: "mobile", defaultValue: "Account is required" });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = async () => {
    if (!canDepositDemo) {
      showError(
        "Error Occurred",
        t("you_dont_have_access_to_do_this_operation", {
          ns: "mobile",
          defaultValue: "You don't have permission to do this operation.",
        })
      );
      return;
    }
    if (!validateForm()) return;

    const amountValue = parseFloat(amount.replace(/,/g, "").trim());
    const toId = toWallet && typeof toWallet === "object" ? toWallet.value : toWallet;
    const selectedTo = toWalletOptions.find((w) => w.value === toId);

    try {
      setProcessing(true);
      setErrors({});

      const res = await createDepositDemoOperation(amountValue, toId);
      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE);

      if (code === AppConstants.responseOk || code === 200) {
        const transactionId = d.TRANSACTION_ID;
        const toSymbol =
          selectedTo?.data?.currencies?.CURRENCY_SYMBOL ||
          selectedTo?.data?.CURRENCY_SYMBOL ||
          "";
        const firstMessage = `${t("you_will_deposit", {
          ns: "mobile",
          defaultValue: "You will deposit",
        })} ${amountValue} ${toSymbol || ""} ${t("to_account", {
          ns: "mobile",
          defaultValue: "to",
        })} ${selectedTo?.label || ""}.`;

        setPasswordDialogData({
          requestToken: transactionId,
          firstMessage,
          secondMessage: "",
        });
        setShowPasswordPopup(true);
      } else if (code === AppConstants.responseNeedPermission) {
        showError("Error Occurred", "You don't have access to do this operation.");
      } else if (code === AppConstants.responseWaiting) {
        showWarning("Action Required", "Financial data should be added to use this method.");
      } else if (code === AppConstants.responseFailed) {
        showError("Error Occurred", d.MESSAGE || "You don't have enough amount.", {
          skipLogout: true,
        });
      } else {
        showError("Error Occurred", d.MESSAGE || "Please try again.");
      }
    } catch (error) {
      showError("Error Occurred", error.message || "Failed to process demo deposit. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handlePasswordDialogClose = async (result) => {
    setShowPasswordPopup(false);
    setPasswordDialogData(null);

    if (result === "ok" || result === "need_admin") {
      clearHomePageInfosClientCache();
      showSuccess(
        "Request Submitted",
        result === "ok"
          ? t("transaction_done", { ns: "mobile", defaultValue: "Deposit completed successfully." })
          : "Deposit is pending admin approval."
      );
      setErrors({});
      setAmount("");
      setToWallet(null);
    } else if (result === "canceled") {
      showWarning(
        "Action Canceled",
        t("transaction_canceled", { ns: "mobile", defaultValue: "Transaction canceled." })
      );
      setErrors({});
      setAmount("");
      setToWallet(null);
    } else if (result === "failed") {
      showError("Error Occurred", t("transaction_failed", { ns: "mobile", defaultValue: "Transaction failed. Please try again." }));
    }

    // Refresh balances in the dropdown.
    try {
      const options = await loadDemoAccounts();
      setToWalletOptions(options);
    } catch (_) {}
  };

  return (
    <div className="cash-in-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && (
          <div className="cash-in-loading-message">{t("loading", { ns: "mobile" })}...</div>
        )}

        <div className="cash-in-container cash-in-container--styled">
          {!canDepositDemo ? (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("you_dont_have_access_to_do_this_operation", { ns: "mobile" })}</p>
              </div>
            </div>
          ) : (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-layout-grid">
                <div className="cash-in-form-panel">
                  <div className="cash-in-panel-header">
                    <h2>{t("demo_deposit", { ns: "mobile", defaultValue: "Demo Deposit" })}</h2>
                    <p>
                      {t("demo_deposit_description", {
                        ns: "mobile",
                        defaultValue: "Add virtual funds to one of your demo trading accounts.",
                      })}
                    </p>
                  </div>

                  <div className="cash-in-form">
                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("amount", { ns: "mobile" })}</label>
                      <div className="cash-in-input-wrapper">
                        <span className="cash-in-input-icon">
                          <i className="fa-solid fa-dollar-sign"></i>
                        </span>
                        <CustomInput
                          type="text"
                          placeholder={t("enter_the_amount", { ns: "mobile" })}
                          value={amount}
                          onChange={handleAmountChange}
                          className={`cash-in-input ${errors.amount ? "cash-in-input-error" : ""}`}
                          disabled={processing}
                        />
                      </div>
                      {errors.amount && <div className="cash-in-field-error">{errors.amount}</div>}
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("to_account", { ns: "mobile" })}</label>
                      <CustomDropdown
                        options={toWalletOptions}
                        value={toWallet}
                        onChange={handleToWalletChange}
                        placeholder={t("select_an_account", { ns: "mobile", defaultValue: "Select an account" })}
                        name="toWallet"
                        className={`cash-in-dropdown ${errors.toWallet ? "dropdown-error" : ""}`}
                        disabled={processing}
                        searchable={true}
                        getDisplayValue={(opt) =>
                          opt && typeof opt === "object" && opt.label != null ? String(opt.label) : ""
                        }
                        renderOption={(option, isSelected) => {
                          const item = option?.data;
                          const flag = item ? getFlagCountryCodeForWallet(item) : null;
                          return (
                            <div
                              className="cash-in-wallet-option"
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              {flag && (
                                <ReactCountryFlag
                                  countryCode={flag}
                                  svg
                                  style={{ width: "20px", height: "15px", objectFit: "cover" }}
                                  title={flag}
                                />
                              )}
                              <span>{option?.label ?? ""}</span>
                              {isSelected && <i className="fa-solid fa-check custom-dropdown-check"></i>}
                            </div>
                          );
                        }}
                        renderValue={(option, placeholderText) => {
                          if (!option) {
                            return (
                              <span className="custom-dropdown-placeholder">
                                {placeholderText || t("select_an_account", { ns: "mobile", defaultValue: "Select an account" })}
                              </span>
                            );
                          }
                          const item = option?.data;
                          const flag = item ? getFlagCountryCodeForWallet(item) : null;
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {flag && (
                                <ReactCountryFlag
                                  countryCode={flag}
                                  svg
                                  style={{ width: "20px", height: "15px", objectFit: "cover" }}
                                  title={flag}
                                />
                              )}
                              <span>{option.label}</span>
                            </div>
                          );
                        }}
                      />
                      {errors.toWallet && <div className="cash-in-field-error">{errors.toWallet}</div>}
                    </div>
                  </div>

                  <div className="cash-in-actions">
                    <CustomButton
                      className="cash-in-proceed-button"
                      variant="primary"
                      onClick={handleProceed}
                      disabled={processing}
                    >
                      <span>
                        {processing ? t("processing", { ns: "mobile" }) + "..." : t("proceed", { ns: "mobile" })}
                      </span>
                    </CustomButton>
                  </div>
                </div>

                <div className="cash-in-side-panel">
                  <div className="cash-in-info-card">
                    <h3>
                      <i className="fa-solid fa-arrow-up" />
                      {t("demo_deposit", { ns: "mobile", defaultValue: "Demo Deposit" })}
                    </h3>
                    <ul>
                      <li>
                        <span>{t("to_account", { ns: "mobile" })}</span>
                        <strong>{(toWallet && toWallet.label) || "-"}</strong>
                      </li>
                      <li>
                        <span>{t("amount", { ns: "mobile" })}</span>
                        <strong>{amount || "-"}</strong>
                      </li>
                    </ul>
                  </div>

                  <div className="cash-in-help-card">
                    <h3>
                      <i className="fa-solid fa-headset" />
                      {t("contactus_title", { ns: "mobile" })}
                    </h3>
                    <p>
                      {t("demo_deposit_help", {
                        ns: "mobile",
                        defaultValue: "Demo funds are virtual and used for practice trading only.",
                      })}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        openSupportChatOnCurrentPage(navigate, location.pathname, location.search)
                      }
                    >
                      {t("about_live_chat_value", { ns: "mobile" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TransactionPasswordDialog
        open={showPasswordPopup && !!passwordDialogData}
        onClose={handlePasswordDialogClose}
        mode="demo_deposit"
        requestToken={passwordDialogData?.requestToken ?? ""}
        firstMessage={passwordDialogData?.firstMessage ?? ""}
        secondMessage={passwordDialogData?.secondMessage ?? ""}
      />
    </div>
  );
}
